import React, { useEffect, useMemo, useState, useContext } from 'react';
import { Box, Paper, Typography, Stack, TextField, FormControl, InputLabel, Select, MenuItem, CircularProgress, Chip } from '@mui/material';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { API_URL } from '../utils/apiConfig';
import { AuthContext } from '../context/AuthContext';

const PREF_COLOR = {
  preferred: 'success.main',
  available: 'info.main',
  unavailable: 'error.main'
};

// Simple array of day names for recurring weekly availability
const DAYS_OF_WEEK = [
  { name: 'Sunday', short: 'Sun', dow: 0 },
  { name: 'Monday', short: 'Mon', dow: 1 },
  { name: 'Tuesday', short: 'Tue', dow: 2 },
  { name: 'Wednesday', short: 'Wed', dow: 3 },
  { name: 'Thursday', short: 'Thu', dow: 4 },
  { name: 'Friday', short: 'Fri', dow: 5 },
  { name: 'Saturday', short: 'Sat', dow: 6 }
];

export default function AvailabilityOverview({ selectedLocation = 'all' }) {
  const { user } = useContext(AuthContext) || {};
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [role, setRole] = useState('');
  const [q, setQ] = useState('');
  const [availByEmp, setAvailByEmp] = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true); setError('');
        const token = localStorage.getItem('token');
        const params = new URLSearchParams();
        const locationId = selectedLocation === 'all' ? (user?.store || null) : selectedLocation;
        if (locationId) params.set('locationId', locationId);
        params.set('limit', '100');
        const resp = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/employees?${params}`, { headers: { Authorization: `Bearer ${token}` }});
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.error || 'Failed to load employees');
        const list = data.data || [];
        setEmployees(list);
        // Fetch availability per employee (simple pass)
        const map = {};
        await Promise.all(list.map(async(emp)=>{
          try {
            const r = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/availability?employeeId=${emp.id}`, { headers: { Authorization: `Bearer ${token}` }});
            const d = await r.json();
            if (r.ok) map[emp.id] = d.data || [];
          } catch {}
        }));
        setAvailByEmp(map);
      } catch (e) {
        setError(e.message);
      } finally { setLoading(false); }
    };
    load();
  }, [user?.store, selectedLocation]);

  const filteredEmployees = useMemo(() => {
    let list = employees;
    if (q) list = list.filter(e => (`${e.first_name} ${e.last_name}`.toLowerCase().includes(q.toLowerCase()) || e.role?.toLowerCase().includes(q.toLowerCase())));
    if (role) list = list.filter(e => e.role === role);
    return list;
  }, [employees, q, role]);

  const minutesBetween = (startStr, endStr) => {
    if (!startStr || !endStr) return 0;
    const [sh, sm] = startStr.split(':').map(Number);
    const [eh, em] = endStr.split(':').map(Number);
    let start = sh * 60 + sm;
    let end = eh * 60 + em;
    if (end <= start) end += 24 * 60; // cross-midnight safeguard
    return Math.max(0, end - start);
  };

  // Prioritize preferences when they overlap at the same time slice.
  // Priority: unavailable > preferred > available.
  const dayPrefHours = (empId, dayOfWeek) => {
    const all = availByEmp[empId] || [];
    const blocks = all.filter(a => a.day_of_week === dayOfWeek);
    const slots = new Array(96).fill('none'); // 96 x 15-min slots

    const mark = (pref, startStr, endStr) => {
      let [sh, sm] = startStr.split(':').map(Number);
      let [eh, em] = endStr.split(':').map(Number);
      let s = sh*60+sm;
      let e = eh*60+em;
      if (e <= s) e += 24*60; // wrapover
      for (let m=s; m<e; m+=15) {
        const idx = Math.floor((m % (24*60))/15);
        const cur = slots[idx];
        if (pref === 'unavailable') slots[idx] = 'unavailable';
        else if (pref === 'preferred') {
          if (cur !== 'unavailable') slots[idx] = 'preferred';
        } else if (pref === 'available') {
          if (cur === 'none') slots[idx] = 'available';
        }
      }
    };

    blocks.forEach(b => mark(b.preference, b.start_time, b.end_time));

    let prefMin=0, availMin=0, unavailMin=0;
    slots.forEach(s => {
      if (s === 'unavailable') unavailMin += 15;
      else if (s === 'preferred') prefMin += 15;
      else if (s === 'available') availMin += 15;
    });

    const toHours = (m) => Math.round((m/60) * 10)/10;
    return {
      preferred: toHours(prefMin),
      availableOther: toHours(availMin),
      availableTotal: toHours(prefMin + availMin),
      unavailable: toHours(unavailMin)
    };
  };

  if (loading) return (<Box display="flex" justifyContent="center" alignItems="center" minHeight={400}><CircularProgress/></Box>);

  return (
    <Paper sx={{ m: 2, p: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Availability Overview</Typography>
          <Typography variant="caption" color="text.secondary">
            Weekly recurring availability patterns (applies to all weeks)
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <TextField size="small" placeholder="Search employees or role" value={q} onChange={(e)=>setQ(e.target.value)} sx={{ minWidth: 200 }} />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Role</InputLabel>
          <Select value={role} onChange={(e)=>setRole(e.target.value)} label="Role">
            <MenuItem value="">All Roles</MenuItem>
            {[...new Set(employees.map(e=>e.role).filter(Boolean))].sort().map(r => (
              <MenuItem key={r} value={r}>{r}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* Legend */}
      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <Chip size="small" label="Preferred (hours)" color="success" />
        <Chip size="small" label="Available (hours)" color="info" />
        <Chip size="small" label="Unavailable (hours)" color="error" />
      </Stack>

      <Box sx={{ overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: `220px repeat(7, minmax(140px, 1fr))`, bgcolor: 'background.paper', position: 'sticky', top: 0, zIndex: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ p: 1, borderRight: '1px solid', borderColor: 'divider', fontWeight: 600, color: 'text.secondary' }}>Employee</Box>
          {DAYS_OF_WEEK.map((day, i) => (
            <Box key={i} sx={{ p: 1, borderRight: i < 6 ? '1px solid' : 'none', borderColor: 'divider', textAlign: 'center' }}>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>{day.short}</Typography>
            </Box>
          ))}
        </Box>
        <Box>
          {filteredEmployees.map(emp => {
            const empLocation = [
              { id: 1, name: 'Chipped Cup' },
              { id: 2, name: 'Mess Hall' },
              { id: 3, name: 'Double Dutch' }
            ].find(l => l.id === emp.location_id);

            return (
            <Box key={emp.id} sx={{ display: 'grid', gridTemplateColumns: `220px repeat(7, minmax(140px, 1fr))`, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ p: 1, borderRight: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{emp.first_name} {emp.last_name}</Typography>
                <Typography variant="caption" color="text.secondary">{emp.role}</Typography>
                {emp.location_id && (
                  <Typography variant="caption" display="block" sx={{ color: 'primary.main', fontSize: '0.7rem' }}>
                    {empLocation?.name || `Location ${emp.location_id}`}
                  </Typography>
                )}
              </Box>
              {DAYS_OF_WEEK.map((day, idx) => {
                const sum = dayPrefHours(emp.id, day.dow);
                return (
                  <Box key={idx} sx={{ p: 0.5, borderRight: idx < 6 ? '1px solid' : 'none', borderColor: 'divider', minHeight: 44 }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: '100%', flexWrap: 'wrap' }}>
                      {sum.availableTotal > 0 && <Chip size="small" label={`Avail ${sum.availableTotal}h`} color="info" sx={{ height: 22 }} />}
                      {sum.preferred > 0 && <Chip size="small" label={`Pref ${sum.preferred}h`} color="success" sx={{ height: 22 }} />}
                      {sum.unavailable > 0 && <Chip size="small" label={`Unavail ${sum.unavailable}h`} color="error" sx={{ height: 22 }} />}
                      {(sum.availableTotal === 0 && sum.unavailable === 0) && (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </Stack>
                  </Box>
                );
              })}
            </Box>
            );
          })}
        </Box>
      </Box>
    </Paper>
  );
}
